import { useEffect, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { UserClaims } from '@okta/okta-auth-js/'
import {
  generateEventTimesOptions,
  getFormattedTimeZoneAbbreviation,
  IAppointmentListItem,
  keyToReadable,
} from '../../utils'
import { shouldDisableDate } from 'shared/AutonomyCalendar/useAutonomyCalendar'
import {
  AppointmentStatus,
  AppointmentType,
} from '@nxcr-org/web-api-interface/lib/appointment_pb'
import { NexusService } from 'global-apis/nexus-service'
import { useQuery } from '@tanstack/react-query'
import { getFormattedDealershipUsers } from 'scheduling/settings/utils'
import { handleDealerships } from '../../useTasks'

export const useAppointmentForm = (appointment: IAppointmentListItem) => {
  const { oktaAuth, authState } = useOktaAuth()
  const [userInfo, setUserInfo] = useState<UserClaims>(undefined)

  const [dealershipId, setDealershipId] = useState<string>()
  const [eventTimesOptions, setEventTimesOptions] = useState([])
  const [dealershipUserOptions, setDealershipUserOptions] = useState([])
  const [disableEventTime, setDisableEventTime] = useState(false)

  const { data: dealershipOptions } = useQuery({
    queryKey: ['/listDealerships'],
    queryFn: async () => {
      const dealershipResponse = await NexusService.listDealerships()
      return handleDealerships(dealershipResponse)
    },
    placeholderData: [],
  })

  const { data: viewTeam } = useQuery({
    queryKey: ['/viewTeam', dealershipId],
    queryFn: async () => {
      const response = await NexusService.viewTeam(dealershipId)
      return getFormattedDealershipUsers(response.dealershipTeamList)
    },
    enabled: Boolean(dealershipId),
  })

  // Handle viewTeam success - convert from onSuccess callback to useEffect
  useEffect(() => {
    if (viewTeam) {
      setDealershipUserOptions(
        viewTeam.map(({ name, id }) => ({ label: name, value: id }))
      )
    }
  }, [viewTeam])

  /*
   * Minimum date is 'today', and the maximum selectable date
   * will be 21 days from the minDate, from 12:00 AM
   * */
  const minDate = new Date()
  minDate.setHours(0)
  minDate.setMinutes(0)
  minDate.setSeconds(0)
  minDate.setMilliseconds(0)

  useEffect(() => {
    if (authState && authState.isAuthenticated) {
      oktaAuth.getUser().then((user) => {
        if (user) {
          setUserInfo(user)
        }
      })
    }
  }, [oktaAuth, authState])

  useEffect(() => {
    setDealershipId(appointment.dealershipId)
  }, [appointment])

  useEffect(() => {
    setEventTimesOptions(generateEventTimesOptions(appointment.date.getTime()))

    const disable = shouldDisableDate(
      appointment.date,
      undefined,
      undefined,
      appointment.date,
      minDate
    )
    setDisableEventTime(disable)
  }, [appointment.date])

  const appointmentTypeOptions = Object.keys(AppointmentType).map(
    (appointmentType) => ({
      label: keyToReadable(appointmentType),
      value: AppointmentType[appointmentType],
    })
  )

  const appointmentStatusOptions = Object.keys(AppointmentStatus).map(
    (appointmentStatus) => ({
      label: keyToReadable(appointmentStatus),
      value: AppointmentStatus[appointmentStatus],
    })
  )

  const eventTimeLabel = `Event time ${getFormattedTimeZoneAbbreviation(
    appointment.timeZone
  )}`

  return {
    dealershipId,
    appointmentTypeOptions,
    appointmentStatusOptions,
    dealershipUserOptions,
    eventTimesOptions,
    eventTimeLabel,
    disableEventTime,
    minDate,
    userInfo,
    setDisableEventTime,
    setDealershipId,
    dealershipOptions,
    viewTeam,
  }
}
